import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, Button, Collapse, makeStyles, Theme } from '@material-ui/core';
import useScrollIntoView from 'common/hooks/useScrollIntoView';
import { IBundleView } from 'modules/services/interfaces/packages';
import { Table, Button as ButtonComponent, DialogComponent } from 'common/components/index';
import { ReactComponent as AngleDownIcon } from 'img/icons/angle-down.svg';
import { ReactComponent as AngleRightIcon } from 'img/icons/angle-right.svg';
import messages from 'common/messages/messages';
import permissionsMessages from 'modules/permissions/messages/messages';
import tableHeaders from 'common/messages/tableHeaders';
import { IHeadCell, ITableRow } from 'common/interfaces/table';
import { DEFAULT_DATE_TIME_FORMAT } from 'common/constants/dateFormats';
import useTimezoneMoment from 'common/hooks/useTimezoneMoment';
import { getAutoRenewalTypeTranslate } from 'modules/services/utils/servicePackage';

const useStyles = makeStyles((theme: Theme) => ({
  sectionBtn: {
    padding: 0,
    color: theme.palette.text.secondary,

    '& svg': {
      width: '0.75rem',
      height: '0.75rem',
      marginRight: theme.spacing(1),
    },
  },
  feeTypeOption: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.darkBackground?.light,
  },
  splittingStatus: {
    display: 'flex',
    alignItems: 'center',
  },
}));

interface IAutoRenewalHistory {
  bundle: IBundleView;
}

const AutoRenewalHistory: FC<IAutoRenewalHistory> = ({ bundle }) => {
  const [showContent, setShowContent] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { scrollRef } = useScrollIntoView(showContent);

  const classes = useStyles();

  const [timezoneMoment] = useTimezoneMoment();

  const firstFiveList = bundle?.autoRenewals.slice(0, 5);

  const headerOptions = useMemo(
    (): IHeadCell[] => [
      {
        id: 'startDate',
        label: <FormattedMessage {...tableHeaders.startDate} />,
        sort: false,
      },
      {
        id: 'endDate',
        label: <FormattedMessage {...tableHeaders.endDate} />,
        sort: false,
        padding: 'none',
      },
      {
        id: 'type',
        label: <FormattedMessage {...tableHeaders.type} />,
        sort: false,
        align: 'left',
      },
    ],
    [],
  );

  const getRows = autoRenewals => {
    if (!autoRenewals) {
      return [];
    }

    return autoRenewals.map(
      (item): ITableRow => {
        return {
          id: `${item.startDate}`,
          cells: [
            {
              label: timezoneMoment(item.startDate).format(DEFAULT_DATE_TIME_FORMAT),
            },
            {
              label: timezoneMoment(item.endDate).format(DEFAULT_DATE_TIME_FORMAT),
            },
            {
              label: getAutoRenewalTypeTranslate(item.type),
            },
          ],
        };
      },
    );
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between">
        <Button
          className={classes.sectionBtn}
          onClick={() => setShowContent(!showContent)}
          startIcon={showContent ? <AngleDownIcon /> : <AngleRightIcon />}
        >
          <FormattedMessage {...messages.historyBtn} />
        </Button>
        {firstFiveList.length > 5 && (
          <Box mr={1.5}>
            <ButtonComponent onClick={() => setIsModalOpen(true)} hasHoverStyle color="primary">
              <FormattedMessage {...permissionsMessages.viewAll} />
            </ButtonComponent>
          </Box>
        )}
      </Box>

      <Collapse in={showContent} ref={scrollRef}>
        <Table
          rows={getRows(firstFiveList)}
          headerOptions={headerOptions}
          hideSearchInput
          showPerPageSelect
          hidePagination
          hideToolbar
          hidePadding
        />
      </Collapse>
      {isModalOpen && (
        <DialogComponent
          title={<FormattedMessage {...messages.historyBtn} />}
          isOpen
          onClose={() => setIsModalOpen(false)}
          size="lg"
          hideFooterButtons
        >
          <Table
            rows={getRows(bundle?.autoRenewals)}
            headerOptions={headerOptions}
            hideSearchInput
            showPerPageSelect
            hidePagination
            hideToolbar
            hidePadding
          />
        </DialogComponent>
      )}
    </Box>
  );
};

export default AutoRenewalHistory;
